import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { setParentCartOwner, clearLegacyParentCart, clearParentCart } from '../utils/parentCart';

/**
 * API base URL.
 *
 * Uses a RELATIVE path so the frontend works on any domain without
 * environment-specific configuration (localhost, uat.cheeco.app, production).
 * The frontend and API are served from the same origin, so /api/v3 resolves
 * correctly on every deployment.
 *
 * If a separate API host is needed in the future, set VITE_API_URL.
 */
const API_BASE = import.meta.env.VITE_API_URL || '/api/v3';

const REQUEST_TIMEOUT_MS = 20000;

const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

/**
 * AuthProviderV3 - Sanctum token management with robust error handling
 *
 * Edge cases handled:
 * - Network failure retry (up to 3 attempts)
 * - Token expiry mid-session (401 → auto-logout)
 * - Back button after logout (clear stale state)
 * - Multiple tab sync (localStorage events)
 * - Request deduplication (prevent double submissions)
 */
export const AuthProvider = ({ children }) => {
  console.log('[AUTH] AuthProvider rendering');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [isOffline, setIsOffline] = useState(false);

  const logout = useCallback(async () => {
    console.log('[AUTH] logout() called');
    if (token) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
      } catch (e) {
        // Ignore errors on logout — we clear locally regardless
      }
    }

    setUser(null);
    setToken(null);
    localStorage.removeItem('cheeco_token');
    localStorage.removeItem('auth.token');
    localStorage.removeItem('auth.user');
    // Clear this user's cart on logout and drop the cart owner so the next
    // parent starts with an empty, isolated cart.
    clearParentCart();
    setParentCartOwner(null);
    console.log('[AUTH] logout() complete — user and token cleared');
  }, [token]);

  const fetchUser = useCallback(async (authToken, retries = 3) => {
    console.log('[AUTH] fetchUser called, token:', authToken?.substring(0, 20) + '...', 'retries:', retries);
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
        },
      });

      console.log('[AUTH] fetchUser response status:', res.status);

      if (!res.ok) {
        if (res.status === 401) {
          console.log('FAILED AT AUTH STEP — fetchUser got 401, calling logout');
          logout();
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      console.log('[AUTH] fetchUser success, user:', data.user?.email, 'role:', data.user?.role);
      setUser(data.user);
      setIsOffline(false);
      localStorage.setItem('auth.token', authToken);
      localStorage.setItem('auth.user', JSON.stringify(data.user));
      // Scope the parent cart to this authenticated user so a different parent
      // logging in on the same browser can never see the previous parent's cart.
      setParentCartOwner(data.user?.id);
      clearLegacyParentCart();
    } catch (err) {
      console.error('[AUTH] fetchUser error:', err.message);

      // Retry on network failure
      if (retries > 0 && navigator.onLine) {
        console.log('[AUTH] fetchUser retrying in 1s...');
        setTimeout(() => fetchUser(authToken, retries - 1), 1000);
        return;
      }

      console.log('FAILED AT AUTH STEP — fetchUser persistent failure, calling logout');
      logout();
    } finally {
      console.log('[AUTH] fetchUser finally — setting loading=false');
      setLoading(false);
    }
  }, [logout]);

  // Initialize from localStorage on mount
  useEffect(() => {
    console.log('[AUTH] Init useEffect running');
    const storedToken = localStorage.getItem('cheeco_token');
    // A Vendor session uses its own dedicated Sanctum token and must never be
    // interpreted as an App\Models\User token: /api/v3/auth/me cannot
    // serialize a Vendor, and treating it as a user would 500 then log out.
    const vendorToken = localStorage.getItem('cheeco_vendor_token');
    if (vendorToken && !storedToken) {
      console.log('[AUTH] Vendor session detected — skipping user bootstrap');
      setLoading(false);

      return;
    }
    console.log('[AUTH] storedToken found:', !!storedToken);
    if (storedToken) {
      setToken(storedToken);
      fetchUser(storedToken);
    } else {
      console.log('[AUTH] No token, setting loading=false');
      setLoading(false);
    }
  }, [fetchUser]);

  // Listen for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for storage changes (multi-tab sync)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'cheeco_token') {
        console.log('[AUTH] Storage event for cheeco_token, newValue:', !!e.newValue);
        if (!e.newValue) {
          // Token removed in another tab — logout here too
          setUser(null);
          setToken(null);
          localStorage.removeItem('auth.token');
          localStorage.removeItem('auth.user');
          clearParentCart();
          setParentCartOwner(null);
        } else if (e.newValue !== token) {
          // Token changed in another tab — update state
          setToken(e.newValue);
          fetchUser(e.newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [token, fetchUser]);

  const requestMagicLink = useCallback(async (email, portal = 'school', intent = 'auth') => {
    let res;
    try {
      res = await fetchWithTimeout(`${API_BASE}/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, portal, intent }),
      });
    } catch (err) {
      const error = err.name === 'AbortError'
        ? new Error('The request is taking too long. Please try again.')
        : new Error('We could not reach the server. Please check your internet connection and try again.');
      error.timeout = err.name === 'AbortError';
      error.network = err.name !== 'AbortError';
      throw error;
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(data.message || 'Failed to send magic link');
      error.status = res.status;
      error.code = data.code || '';
      error.data = data;
      error.retryAfter = data.retry_after;
      throw error;
    }
    return data;
  }, []);

  const verifyMagicLink = useCallback(async (rawToken, portal = '') => {
    console.log('[AUTH] verifyMagicLink called, rawToken:', rawToken);
    let res;
    try {
      res = await fetchWithTimeout(`${API_BASE}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: rawToken, portal }),
      });
    } catch (err) {
      const error = err.name === 'AbortError'
        ? new Error('The link check is taking too long. Please try again.')
        : new Error('We could not reach the server. Please check your internet connection and try again.');
      error.timeout = err.name === 'AbortError';
      error.network = err.name !== 'AbortError';
      throw error;
    }

    console.log('[AUTH] verifyMagicLink response status:', res.status);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.log('FAILED AT AUTH STEP — verifyMagicLink API error:', data.message);
      const error = new Error(data.message || 'Invalid or expired link');
      error.status = res.status;
      error.code = data.code || '';
      error.data = data;
      throw error;
    }

    if (data.token) {
      console.log('[AUTH] verifyMagicLink storing Sanctum token');
      setToken(data.token);
      localStorage.setItem('cheeco_token', data.token);
      localStorage.setItem('auth.token', data.token);
      setUser(data.user);
      localStorage.setItem('auth.user', JSON.stringify(data.user));
      // Scope the parent cart to the newly authenticated user.
      setParentCartOwner(data.user?.id);
      clearLegacyParentCart();
      console.log('[AUTH] verifyMagicLink user set:', data.user?.email, 'role:', data.user?.role);
    }

    return data;
  }, []);

  const completeOnboarding = useCallback(async (profile) => {
    if (!token) {
      throw new Error('No active session');
    }

    const res = await fetch(`${API_BASE}/auth/onboarding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
      body: JSON.stringify(profile),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || 'Failed to complete onboarding');
    }

    setUser(data.user);
    localStorage.setItem('auth.user', JSON.stringify(data.user));
    return data;
  }, [token]);

  const logoutAll = useCallback(async () => {
    if (!token) return;

    try {
      await fetch(`${API_BASE}/auth/logout-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
    } catch (e) {
      // Ignore
    }

    setUser(null);
    setToken(null);
    localStorage.removeItem('cheeco_token');
    // Clear this user's cart on logout-all and drop the cart owner.
    clearParentCart();
    setParentCartOwner(null);
  }, [token]);

  const getAuthHeaders = useCallback(() => {
    if (!token) return {};
    return {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    };
  }, [token]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.user) {
        setUser(data.user);
        localStorage.setItem('auth.user', JSON.stringify(data.user));
      }
    } catch (e) {
      console.error('[AUTH] refresh failed:', e.message);
    }
  }, [token]);

  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: !!user,
    isOnboarding: user?.onboarding_step === 'welcome',
    isOffline,
    requestMagicLink,
    verifyMagicLink,
    completeOnboarding,
    refresh,
    logout,
    logoutAll,
    getAuthHeaders,
    token,
  }), [user, loading, isOffline, token, requestMagicLink, verifyMagicLink, completeOnboarding, refresh, logout, logoutAll, getAuthHeaders]);

  console.log('[AUTH] AuthProvider rendering with state:', { loading, isAuthenticated: !!user, hasUser: !!user });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
